<template>
    <WidgetSection
        ref="WidgetSection"
        :loaded="loaded"
        :loading="loading"
        v-if="!hide"
        :defaultToggle="defaultToggle"
        @toggle="handleToggle"
        @loading-start="handleLoadingStart"
        :cssClass="`slider-type-1 ${cssClassWidgetSection}`"
        loadingText="common.loading_1"
        :paywall="paywall"
    >
        <template v-slot:section-title>
            <div class="title">
                {{ $t('components.prospect_news.name')}}
            </div>
        </template>

        <template v-slot:section-actions>
            <div class="swiper-navigation">
                <div :class="`swiper-button-prev-${uniqueId}`"></div>
                <div :class="`swiper-button-next-${uniqueId}`"></div>
            </div>
        </template>

        <template v-slot:section-content>
            <div class="row row-slider">
                <swiper-container ref="swiper" class="swiper-container" init="false">
                    <swiper-slide v-for="(newsItem, index) in news.details" :key="index">
                        <component :is="newsItem.url ? 'a' : 'div'" :href="newsItem.url || undefined" target="_blank" rel="noreferrer external" class="slide-container">
                            <div class="bar">
                                <div class="left">
                                    <CompanyLogo 
                                        :domain="newsItem.domain ? newsItem.domain : null"
                                        :company_name="newsItem.domain ? newsItem.domain : null"
                                    />
                                </div>
                                <div class="right">
                                    <div class="title" v-if="newsItem.title" v-html="highlightDescription(newsItem.title)"></div>
                                    <div class="date" v-if="newsItem.date">{{ formatDateReverse(newsItem.date) }}</div>
                                </div>
                            </div>
                            <div class="description news" v-if="newsItem.description" v-html="highlightDescription(newsItem.description || '')"></div>
                            <div class="url" v-if="newsItem.url">{{ newsItem.url }}</div>
                        </component>
                    </swiper-slide>
                </swiper-container>
            </div>
        </template>
    </WidgetSection>
</template>

<script>
    import { useProspectStore } from '@/stores/prospect';
    import { useAccountStore } from '@/stores/account';
    import { fetchUrl } from '@/services/ppApi';
    import { nextTick } from 'vue';
    import WidgetSection from '@/components/sections/WidgetSection';
    import CompanyLogo from '@/components/elements/misc/CompanyLogo';
    import { formatDateReverse } from '@/utils/formatting';
    import i18n from "@/plugins/i18n";
    import { useToastStore } from '@/stores/utils/toast';

    export default {
        name: "ProspectNews",
        components: {
            WidgetSection,
            CompanyLogo
        },
        props: {
            auto: {
                type: Boolean,
                default: false
            },
            defaultToggle: {
                type: Boolean,
                default: false
            },
            paywall: {
                type: Boolean,
                default: false
            },
            cssClassWidgetSection: {
                type: String,
                required: false
            },
            narrowVersion: {
                type: Boolean,
                default: false
            },
            showLoadingToasts: {
                type: Boolean,
                default: true
            }
        },
        data() {
            return {
                loading: false,
                loaded: false,
                abortController: null,
                timeoutId: null,
                uniqueId: Math.random().toString(36).substr(2, 9),
                isActive: true
            }
        },
        computed: {
            news() {
                return useProspectStore().news;
            },
            paywallActive() {
                return this.paywall && useAccountStore().paywall;
            },
            hide() {
                return !this.news || (this.loaded && (!this.news.details || this.news.details.length < 1));
            }
        },
        methods: {
            formatDateReverse,
            highlightDescription(text) {
                return text.replace(/\*\*(.*?)\*\*/g, '<span class="highlight">$1</span>');
            },
            async fetchNews() {
                if (!this.news || (this.news.api_urls && this.news.api_urls.length < 1) || this.loaded) {
                    return;
                }

                if (this.abortController) {
                    this.abortController.abort();
                    this.abortController = null;
                }

                this.abortController = new AbortController();
                this.loading = true;

                try {
                    const response = await fetchUrl(this.news.api_urls[0], this.abortController.signal);
                    this.news.details = response.data.news || '';

                    if(this.showLoadingToasts) {
                        if(this.news?.details?.length > 0) {
                            useToastStore().addToast(i18n.global.t(`toasts.success.prospect_news`), 'success');
                        } else {
                            useToastStore().addToast(i18n.global.t(`toasts.error.prospect_news`), 'error'); 
                        }
                    }
                } catch {
                    this.news?.details ? this.news.details = null : '';
                } finally {
                    this.loading = false;
                    this.loaded = true;
                    this.abortController = null;
                    await nextTick();
                    if (this.isActive) {
                        this.initSwiper();
                    }
                }
            },
            initSwiper() {
                if(!this.$refs.swiper) { return; }
                
                const swiperEl = this.$refs.swiper;
                const swiperParams = {
                    slidesPerView: 1,
                    spaceBetween: 24,
                    navigation: {
                        nextEl: `.swiper-button-next-${this.uniqueId}`,
                        prevEl: `.swiper-button-prev-${this.uniqueId}`
                    }
                };

                swiperParams.breakpoints =  this.narrowVersion ?
                                            {
                                                769: {
                                                    slidesPerView: 1.2,
                                                    slidesPerGroup: 1
                                                },
                                                1240: {
                                                    slidesPerView: 2,
                                                    slidesPerGroup: 2
                                                }
                                            } : {
                                                769: {
                                                    slidesPerView: 2,
                                                    slidesPerGroup: 2
                                                },
                                                992: {
                                                    slidesPerView: 3,
                                                    slidesPerGroup: 3
                                                }
                                            };
                
                const existing = swiperEl.swiper;
                if (existing && !existing.destroyed) {
                    existing.update();
                    existing.navigation?.update();
                    return;
                }

                Object.assign(swiperEl, swiperParams);
                swiperEl.initialize();
            },
            handleToggle(isVisible) {
                const swiperEl = this.$refs.swiper;
                if (isVisible) {
                    this.$nextTick(() => {
                        this.initSwiper();
                    });
                } else if (swiperEl?.swiper && !swiperEl.swiper.destroyed) {
                    swiperEl.swiper.destroy(true, true);
                }
            },
            async handleLoadingStart(isVisible) {
                if(this.paywallActive) { return; }

                if (!this.loaded) {
                    await this.fetchNews();
                    if(!this.hide && this.$refs.WidgetSection) {
                        this.$refs.WidgetSection.toggleSection();
                    }
                } else {
                    this.handleToggle(isVisible);
                }
            },
        },
        mounted() {
            if(!this.paywallActive &&  !this.loaded && (this.auto || this.defaultToggle)) {
                this.loading = true;
                this.timeoutId = setTimeout(() => {
                    this.fetchNews();
                }, 300);
            }
        },
        beforeUnmount() {
            this.isActive = false;

            if (this.$refs.swiper?.swiper && !this.$refs.swiper.swiper.destroyed) {
                this.$refs.swiper.swiper.destroy(true, true);
            }
            if (this.abortController) {
                this.abortController.abort();
                this.abortController = null;
            }
            if (this.timeoutId) {
                clearTimeout(this.timeoutId);
            }
        }
    }
</script>